import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend as ChartLegend } from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import MapGL, { Marker, Popup, MapRef } from 'react-map-gl/mapbox';
import DateRangeFilter, { DateRangeValue } from 'component/DateRangeFilterDropdown';
import MonitorService from 'service/MonitorService';
import { MAPBOX_TOKEN } from 'config/Mapbox';
import { ActiveUserMapResp, UserGrowthDashboardResp } from 'domain/payload/response/monitor/UserGrowthDashboardResp';
import CustomDropdown, { DropdownOption } from 'component/CustomDropdown';

ChartJS.register(ArcElement, ChartTooltip, ChartLegend);

// Color palette
const COLORS = {
  primary: '#3b82f6',      // Blue
  primaryLight: '#dbeafe', // Soft Blue
  success: '#10b981',      // Green
  successLight: '#d1fae5', // Soft Green
  warning: '#f59e0b',      // Orange/Yellow
  warningLight: '#fef3c7', // Soft Orange
  danger: '#ef4444',       // Red
  dangerLight: '#fee2e2',  // Soft Red
  info: '#06b6d4',         // Cyan
  purple: '#8b5cf6',       // Purple
  purpleLight: '#f3e8ff',  // Soft Purple
  grayText: '#64748b',     // Muted text
  darkText: '#1e293b',     // Dark slate text
  border: '#e2e8f0',       // Light border
  bgLight: '#f8fafc',      // Light gray slate
};

type UserType = 'moreAccess' | 'perfectStore';

// Interface for metrics
interface GrowthMetrics {
  activeLastHour: number;
  dau: number;
  wau: number;
  mau: number;
  peakActiveThisMonth: number;
  peakActiveDate: string;
  stickinessRatio: number;
  churnRate: number;
  mobileActive: number;
  webActive: number;
  dauTrendVsPrev: number; // percentage change vs previous period
  wauTrendVsPrev: number;
  mauTrendVsPrev: number;
  churnTrendVsPrev: number;
}


interface DeviceData {
  device: string;
  count: number;
  percentage?: number;
  color?: string;
}

const getOrCreateTooltip = (chart: any) => {
  let tooltipEl = document.getElementById('growth-monitor-chart-tooltip');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.id = 'growth-monitor-chart-tooltip';
    tooltipEl.style.position = 'fixed';
    tooltipEl.style.pointerEvents = 'none';
    tooltipEl.style.zIndex = '9999';
    tooltipEl.style.background = '#1e293b';
    tooltipEl.style.color = 'white';
    tooltipEl.style.borderRadius = '8px';
    tooltipEl.style.padding = '8px 12px';
    tooltipEl.style.fontSize = '0.75rem';
    tooltipEl.style.whiteSpace = 'nowrap';
    tooltipEl.style.opacity = '0';
    tooltipEl.style.transition = 'opacity 0.1s ease';
    document.body.appendChild(tooltipEl);
  }

  return tooltipEl;
};

const externalTooltipHandler = (context: any) => {
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = '0';
    return;
  }

  if (tooltip.body) {
    const lines = tooltip.body.map((b: any) => b.lines).flat();
    const colors = tooltip.labelColors;

    const rows = lines.map((line: string, i: number) => {
      const bg = colors?.[i]?.backgroundColor || '#94a3b8';
      return `
        <div style="display:flex; align-items:center; gap:6px;">
          <span style="
            width:8px; height:8px; border-radius:50%;
            background:${bg}; flex-shrink:0; display:inline-block;
          "></span>
          <span>${line}</span>
        </div>
      `;
    });

    tooltipEl.innerHTML = rows.join('');
  }

  const canvasRect = chart.canvas.getBoundingClientRect();
  const tooltipRect = tooltipEl.getBoundingClientRect();

  let left = canvasRect.left + tooltip.caretX + 12;
  let top = canvasRect.top + tooltip.caretY - tooltipRect.height / 2;

  if (left + tooltipRect.width > window.innerWidth) {
    left = canvasRect.left + tooltip.caretX - tooltipRect.width - 12;
  }
  if (left < 0) left = 8;

  tooltipEl.style.left = left + 'px';
  tooltipEl.style.top = top + 'px';
  tooltipEl.style.opacity = '1';
};

const UserGrowthMonitor: React.FC = () => {
  const { profile } = useSelector((state: any) => state.auth);
  const token = profile?.data?.token;
  const monitorService = useMemo(() => MonitorService(), []);

  const [userType, setUserType] = useState<UserType>('moreAccess');
  const [timeRange, setTimeRange] = useState<DateRangeValue>({ startDate: null, endDate: null });
  const [loading, setLoading] = useState<boolean>(false);
  const [autoRefresh, setAutoRefresh] = useState<number>(0);
  const [lastRefresh, setLastRefresh] = useState<string>('');
  const [dashboardData, setDashboardData] = useState<UserGrowthDashboardResp | null>(null);
  const [showUnknown, setShowUnknown] = useState<boolean>(false);

  // Map state
  const mapRef = useRef<MapRef | null>(null);
  const [selectedUserMap, setSelectedUserMap] = useState<ActiveUserMapResp | null>(null);
  const [mapCategoryFilter, setMapCategoryFilter] = useState<string>('all');
  const [mapSearchQuery, setMapSearchQuery] = useState<string>('');

  const AUTO_REFRESH_OPTIONS: DropdownOption[] = [
    { id: 0, name: 'Off' },
    { id: 10000, name: '10 seconds' },
    { id: 300000, name: '5 minutes' },
    { id: 600000, name: '10 minutes' },
  ];

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await monitorService.userGrowthDashboard(
        token,
        userType,
        timeRange.startDate,
        timeRange.endDate
      );
      if (res.data) {
        setDashboardData(res.data);
      }
      const now = new Date();
      setLastRefresh(
        now.toLocaleTimeString('id-ID', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        })
      );
    } catch (err) {
      console.error('Error fetching user growth dashboard data', err);
    } finally {
      setLoading(false);
    }
  }, [token, userType, timeRange.startDate, timeRange.endDate, monitorService]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Manual refresh helper
  const handleManualRefresh = () => {
    fetchData();
  };

  // Handle auto-refresh interval
  useEffect(() => {
    if (autoRefresh === 0) return;
    const interval = setInterval(() => {
      fetchData();
    }, autoRefresh);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  // Handle filter changes
  const handleFilterChange = (val: DateRangeValue) => {
    setTimeRange(val);
  };

  const defaultDashboardData = useMemo<UserGrowthDashboardResp>(() => ({
    summary: {
      concurrency: 0,
      dau: 0,
      wau: 0,
      mau: 0,
      stickinessRatio: 0,
      churnRate: 0,
      peakActiveUsers: 0,
      platformSplit: { mobileCount: 0, mobilePercentage: 0, webCount: 0, webPercentage: 0 }
    },
    activeUserMaps: [],
    trend: [],
    breakdowns: { brands: [], osVersions: [], appVersions: [], banks: [] },
    regional: []
  }), []);

  const data = dashboardData || defaultDashboardData;

  const activeUserMaps = useMemo<ActiveUserMapResp[]>(() => {
    return data.activeUserMaps || [];
  }, [data.activeUserMaps]);

  const validActiveUserMaps = useMemo<ActiveUserMapResp[]>(() => {
    return activeUserMaps.filter(
      (m) => m.latitude && m.longitude && (m.latitude !== 0 || m.longitude !== 0)
    );
  }, [activeUserMaps]);

  const categoryStats = useMemo(() => {
    const stats: Record<string, { validCount: number; emptyCount: number; totalCount: number; colorHex: string }> = {};
    activeUserMaps.forEach((m) => {
      const cat = (m.category || 'other').toLowerCase();
      if (!stats[cat]) {
        stats[cat] = { validCount: 0, emptyCount: 0, totalCount: 0, colorHex: m.colorHex || '#3b82f6' };
      }
      stats[cat].totalCount += 1;
      const isValid = Boolean(m.latitude && m.longitude && (m.latitude !== 0 || m.longitude !== 0));
      if (isValid) {
        stats[cat].validCount += 1;
      } else {
        stats[cat].emptyCount += 1;
      }
      if (m.colorHex) {
        stats[cat].colorHex = m.colorHex;
      }
    });
    return stats;
  }, [activeUserMaps]);

  const totalEmptyUserMapsCount = useMemo(() => {
    return activeUserMaps.length - validActiveUserMaps.length;
  }, [activeUserMaps.length, validActiveUserMaps.length]);

  const filteredUserMaps = useMemo(() => {
    return validActiveUserMaps.filter((m) => {
      const matchCategory =
        mapCategoryFilter === 'all' ||
        (m.category || '').toLowerCase() === mapCategoryFilter.toLowerCase();
      const query = mapSearchQuery.trim().toLowerCase();
      const matchSearch =
        !query ||
        m.name?.toLowerCase().includes(query) ||
        m.phone?.toLowerCase().includes(query) ||
        m.email?.toLowerCase().includes(query);
      return matchCategory && matchSearch;
    });
  }, [validActiveUserMaps, mapCategoryFilter, mapSearchQuery]);

  const handleFitMapBounds = useCallback(() => {
    if (!mapRef.current || filteredUserMaps.length === 0) return;
    const lngs = filteredUserMaps.map((m) => m.longitude);
    const lats = filteredUserMaps.map((m) => m.latitude);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);

    if (minLng === maxLng && minLat === maxLat) {
      mapRef.current.flyTo({ center: [minLng, minLat], zoom: 12, duration: 600 });
    } else {
      mapRef.current.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        { padding: 60, duration: 800, maxZoom: 14 }
      );
    }
  }, [filteredUserMaps]);

  useEffect(() => {
    handleFitMapBounds();
  }, [handleFitMapBounds]);

  useEffect(() => {
    return () => {
      document.getElementById('growth-monitor-chart-tooltip')?.remove();
    };
  }, []);

  const metricsData = useMemo<GrowthMetrics>(() => {
    const s = data.summary;
    return {
      activeLastHour: s.concurrency || 0,
      dau: s.dau || 0,
      wau: s.wau || 0,
      mau: s.mau || 0,
      peakActiveThisMonth: s.peakActiveUsers || 0,
      peakActiveDate: 'This month',
      stickinessRatio: s.stickinessRatio || 0,
      churnRate: s.churnRate || 0,
      mobileActive: s.platformSplit?.mobileCount || 0,
      webActive: s.platformSplit?.webCount || 0,
      dauTrendVsPrev: 0,
      wauTrendVsPrev: 0,
      mauTrendVsPrev: 0,
      churnTrendVsPrev: 0,
    };
  }, [data.summary]);

  const getRecalculatedData = (rawList: DeviceData[]) => {
    const total = rawList.reduce((acc, curr) => acc + curr.count, 0);
    return rawList.map(item => ({
      ...item,
      percentage: total > 0 ? parseFloat(((item.count / total) * 100).toFixed(1)) : 0
    }));
  };

  const brandData = useMemo<DeviceData[]>(() => {
    const raw = data.breakdowns?.brands || [];
    const filtered = showUnknown ? raw : raw.filter((d: DeviceData) => d.device.toLowerCase() !== 'unknown');
    return getRecalculatedData(filtered);
  }, [data.breakdowns?.brands, showUnknown]);

  const osData = useMemo<DeviceData[]>(() => {
    const raw = data.breakdowns?.osVersions || [];
    const filtered = showUnknown ? raw : raw.filter((d: DeviceData) => d.device.toLowerCase() !== 'unknown');
    return getRecalculatedData(filtered);
  }, [data.breakdowns?.osVersions, showUnknown]);

  const appVersionData = useMemo<DeviceData[]>(() => {
    const raw = data.breakdowns?.appVersions || [];
    const filtered = showUnknown ? raw : raw.filter((d: DeviceData) => d.device.toLowerCase() !== 'unknown');
    return getRecalculatedData(filtered);
  }, [data.breakdowns?.appVersions, showUnknown]);

  const bankData = useMemo<DeviceData[]>(() => {
    const raw = data.breakdowns?.banks || [];
    const filtered = showUnknown ? raw : raw.filter((d: DeviceData) => d.device.toLowerCase() !== 'unknown');
    return getRecalculatedData(filtered);
  }, [data.breakdowns?.banks, showUnknown]);

  const brandTotalCount = useMemo(() => brandData.reduce((acc, curr) => acc + curr.count, 0), [brandData]);
  const osTotalCount = useMemo(() => osData.reduce((acc, curr) => acc + curr.count, 0), [osData]);
  const appTotalCount = useMemo(() => appVersionData.reduce((acc, curr) => acc + curr.count, 0), [appVersionData]);
  const bankTotalCount = useMemo(() => bankData.reduce((acc, curr) => acc + curr.count, 0), [bankData]);

  // Generate chart data for the growth trends (Line Chart)
  const trendChartData = useMemo(() => {
    const labels = data.trend?.map((t: any) => t.date) || [];
    const activeData = data.trend?.map((t: any) => t.activeUsers) || [];
    const registeredData = data.trend?.map((t: any) => t.registeredUsers) || [];

    return {
      labels,
      datasets: [
        {
          label: 'Active Users',
          data: activeData,
          borderColor: COLORS.primary,
          backgroundColor: 'rgba(59, 130, 246, 0.06)',
          fill: true,
          tension: 0.35,
          borderWidth: 3,
          pointRadius: 0,
          pointBackgroundColor: COLORS.primary,
          pointHoverRadius: 6,
        },
        {
          label: 'Total Registered Users',
          data: registeredData,
          borderColor: COLORS.purple,
          backgroundColor: 'transparent',
          borderDash: [5, 5],
          tension: 0.25,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: COLORS.purple,
          yAxisID: 'y1',
        },
      ],
    };
  }, [data.trend]);

  const trendChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          font: { family: 'Inter, system-ui, sans-serif', size: 11, weight: 'bold' },
          color: COLORS.darkText,
        },
      },
      tooltip: {
        backgroundColor: '#1e293b',
        titleFont: { family: 'Inter, sans-serif', size: 12, weight: 'bold' },
        bodyFont: { family: 'Inter, sans-serif', size: 12 },
        padding: 10,
        cornerRadius: 8,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { family: 'Inter, sans-serif', size: 10 },
          color: COLORS.grayText,
        },
      },
      y: {
        border: { dash: [4, 4] },
        grid: { color: COLORS.border },
        ticks: {
          font: { family: 'Inter, sans-serif', size: 10 },
          color: COLORS.grayText,
          callback: (value: any) => value.toLocaleString('id-ID'),
        },
        title: {
          display: true,
          text: 'Active Users (Count)',
          font: { family: 'Inter, sans-serif', size: 10, weight: 'bold' },
          color: COLORS.grayText,
        },
      },
      y1: {
        position: 'right' as const,
        grid: { display: false },
        ticks: {
          font: { family: 'Inter, sans-serif', size: 10 },
          color: COLORS.grayText,
          callback: (value: any) => value.toLocaleString('id-ID'),
        },
        title: {
          display: true,
          text: 'Total Registered',
          font: { family: 'Inter, sans-serif', size: 10, weight: 'bold' },
          color: COLORS.grayText,
        },
      },
    },
  } as any;

  const verticalHoverPlugin = {
    id: 'verticalHoverBg',
    afterDraw: (chart: any) => {
      if (chart.tooltip?._active?.length) {
        const activePoint = chart.tooltip._active[0];
        const ctx = chart.ctx;
        const chartArea = chart.chartArea;
        const x = activePoint.element.x;

        ctx.save();

        // 1. Draw a soft column highlight background
        ctx.fillStyle = 'rgba(59, 130, 246, 0.04)'; // very soft primary blue highlight
        const meta = chart.getDatasetMeta(0);
        if (meta && meta.data) {
          const pointsCount = meta.data.length;
          let width = 30;
          if (pointsCount > 1) {
            width = (chartArea.right - chartArea.left) / (pointsCount - 1);
          }
          ctx.fillRect(x - width / 2, chartArea.top, width, chartArea.bottom - chartArea.top);
        }

        // 2. Draw a clean dashed vertical line in the middle
        ctx.beginPath();
        ctx.setLineDash([4, 4]);
        ctx.moveTo(x, chartArea.top);
        ctx.lineTo(x, chartArea.bottom);
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'rgba(59, 130, 246, 0.2)'; // semi-transparent primary blue
        ctx.stroke();

        ctx.restore();
      }
    }
  };

  // Brand Specs Doughnut Data
  const brandChartData = useMemo(() => ({
    labels: brandData.map((d) => d.device),
    datasets: [{
      data: brandData.map((d) => d.count),
      backgroundColor: brandData.map((d) => d.color),
      borderWidth: 0,
      cutout: '70%',
    }],
  }), [brandData]);

  // OS Version Specs Doughnut Data
  const osChartData = useMemo(() => ({
    labels: osData.map((d) => d.device),
    datasets: [{
      data: osData.map((d) => d.count),
      backgroundColor: osData.map((d) => d.color),
      borderWidth: 0,
      cutout: '70%',
    }],
  }), [osData]);

  // App Version Specs Doughnut Data
  const appVersionChartData = useMemo(() => ({
    labels: appVersionData.map((d) => d.device),
    datasets: [{
      data: appVersionData.map((d) => d.count),
      backgroundColor: appVersionData.map((d) => d.color),
      borderWidth: 0,
      cutout: '70%',
    }],
  }), [appVersionData]);

  // Disbursement Bank Specs Doughnut Data
  const bankChartData = useMemo(() => ({
    labels: bankData.map((d) => d.device),
    datasets: [{
      data: bankData.map((d) => d.count),
      backgroundColor: bankData.map((d) => d.color),
      borderWidth: 0,
      cutout: '70%',
    }],
  }), [bankData]);

  const deviceChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: externalTooltipHandler,
        callbacks: {
          label: (context: any) => {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value.toLocaleString('id-ID')} (${percentage}%)`;
          },
        },
      },
    },
  };

  return (
    <div
      className="page-wrapper position-relative"
      style={{ backgroundColor: COLORS.bgLight, minHeight: '100vh', padding: '24px 20px' }}
    >
      {/* Dynamic styling for modern minimalist hover transitions and animations */}
      <style>{`
        .growth-card {
          border-radius: 16px;
          background: #ffffff;
          border: 1px solid rgba(226, 232, 240, 0.8);
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .growth-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px -5px rgba(0, 0, 0, 0.04);
          border-color: rgba(59, 130, 246, 0.2);
        }
        .pulse-dot {
          width: 8px;
          height: 8px;
          background-color: ${COLORS.success};
          border-radius: 50%;
          display: inline-block;
          animation: pulse 1.8s infinite ease-in-out;
        }
        @keyframes pulse {
          0% {
            transform: scale(0.85);
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.6);
          }
          70% {
            transform: scale(1.1);
            box-shadow: 0 0 0 8px rgba(16, 185, 129, 0);
          }
          100% {
            transform: scale(0.85);
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
          }
        }
        .tab-btn {
          border: none;
          background: transparent;
          color: ${COLORS.grayText};
          padding: 8px 16px;
          font-size: 0.85rem;
          font-weight: 600;
          border-radius: 10px;
          transition: all 0.2s ease;
        }
        .tab-btn.active {
          background: #ffffff;
          color: ${COLORS.primary};
          box-shadow: 0 2px 8px -1px rgba(0, 0, 0, 0.08);
        }
        .table-custom tbody tr {
          transition: background-color 0.15s ease;
        }
        .table-custom tbody tr:hover {
          background-color: ${COLORS.bgLight};
        }
        .progress-micro {
          height: 6px;
          border-radius: 4px;
          background-color: ${COLORS.border};
          overflow: hidden;
        }
        .progress-micro-fill {
          height: 100%;
          border-radius: 4px;
          transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .info-tooltip-container {
          position: relative;
          display: inline-block;
          cursor: help;
          margin-left: 4px;
          color: ${COLORS.grayText};
        }
        .info-tooltip-text {
          visibility: hidden;
          width: 220px;
          background-color: #1e293b;
          color: #fff;
          text-align: center;
          border-radius: 6px;
          padding: 8px 12px;
          position: absolute;
          z-index: 10;
          bottom: 125%;
          left: 50%;
          transform: translateX(-50%);
          opacity: 0;
          transition: opacity 0.2s ease;
          font-size: 0.75rem;
          line-height: 1.35;
          font-weight: normal;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        }
        .info-tooltip-container:hover .info-tooltip-text {
          visibility: visible;
          opacity: 1;
        }
        .info-tooltip-text::after {
          content: "";
          position: absolute;
          top: 100%;
          left: 50%;
          margin-left: -5px;
          border-width: 5px;
          border-style: solid;
          border-color: #1e293b transparent transparent transparent;
        }
      `}</style>

      {/* Loading Overlay */}
      {loading && (
        <div
          className="top-0 position-absolute start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{ background: 'rgba(255, 255, 255, 0.6)', zIndex: 100, borderRadius: '16px' }}
        >
          <div className="spinner-border text-primary" role="status" style={{ width: 50, height: 50 }}>
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      <div className="mb-4 d-flex flex-wrap justify-content-between align-items-center gap-3">
        <div>
          <h4 className="mb-1 fw-bold text-dark" style={{ letterSpacing: '-0.75px' }}>
            Growth Monitor
          </h4>
        </div>

        <div className="p-1 d-flex bg-light border rounded-3" style={{ borderRadius: '12px' }}>
          <button
            type="button"
            className={`tab-btn ${userType === 'moreAccess' ? 'active' : ''}`}
            onClick={() => {
              setLoading(true);
              setUserType('moreAccess');
            }}
          >
            <i className="fa-solid fa-users-gear me-2"></i>More Access
          </button>
          <button
            type="button"
            className={`tab-btn ${userType === 'perfectStore' ? 'active' : ''}`}
            onClick={() => {
              setLoading(true);
              setUserType('perfectStore');
            }}
          >
            <i className="fa-solid fa-store me-2"></i>Perfect Store
          </button>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4 p-3" style={{ borderRadius: '16px', background: '#ffffff' }}>
        <div className="row g-3 align-items-center justify-content-between">
          <div className="col-12 col-md-8 d-flex flex-wrap align-items-center gap-3">
            <div style={{ minWidth: '220px' }}>
              <DateRangeFilter onChange={handleFilterChange} />
            </div>
          </div>

          <div className="col-12 col-md-4 d-flex align-items-center justify-content-md-end gap-3">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted fw-semibold" style={{ fontSize: '0.8rem' }}>Auto Refresh</span>
              <CustomDropdown
                value={autoRefresh}
                options={AUTO_REFRESH_OPTIONS}
                onChange={(opt) => setAutoRefresh(Number(opt.id))}
                containerClassName="me-1"
                align="right"
                buttonStyle={{
                  width: "130px",
                  height: "50px",
                  padding: "6px 12px"
                }}
              />
            </div>
            <button
              type="button"
              className="btn btn-light border-0 shadow-sm d-flex align-items-center justify-content-center"
              onClick={handleManualRefresh}
              style={{ width: '38px', height: '38px', borderRadius: '10px', background: '#f1f5f9', color: '#475569' }}
              title="Manual Refresh"
            >
              <i className="fa fa-rotate"></i>
            </button>
            <span className="text-muted text-nowrap d-none d-sm-inline" style={{ fontSize: '0.75rem' }}>
              Updated: <span className="fw-semibold text-dark">{lastRefresh || '-'}</span>
            </span>
          </div>
        </div>
      </div>
      <div className="card border-0 shadow-sm mb-4 p-4" style={{ borderRadius: '16px', background: '#ffffff' }}>
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
          <div>
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.75px' }}>
                Active Users Map
              </span>
              <span className="badge bg-light text-primary border px-2 py-1" style={{ fontSize: '0.7rem' }}>
                <i className="fa-solid fa-location-dot me-1"></i>
                {filteredUserMaps.length} / {validActiveUserMaps.length} Users
              </span>
            </div>
            <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>
              Active user location distribution by activity
            </p>
          </div>

          <div className="d-flex flex-wrap align-items-center gap-2">
            <div className="position-relative" style={{ width: '220px' }}>
              <input
                type="text"
                className="form-control form-control-sm pe-4 bg-light border-0"
                placeholder="Search name / phone / email..."
                style={{ borderRadius: '8px', fontSize: '0.75rem' }}
                value={mapSearchQuery}
                onChange={(e) => setMapSearchQuery(e.target.value)}
              />
              {mapSearchQuery && (
                <button
                  type="button"
                  className="btn btn-sm btn-link text-muted position-absolute end-0 top-50 translate-middle-y p-0 me-2 text-decoration-none"
                  onClick={() => setMapSearchQuery('')}
                  style={{ fontSize: '0.7rem' }}
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              )}
            </div>
            <button
              type="button"
              className="btn btn-sm btn-light border-0 shadow-sm text-dark fw-semibold d-flex align-items-center gap-1"
              onClick={handleFitMapBounds}
              style={{ borderRadius: '8px', fontSize: '0.75rem', padding: '5px 10px', background: '#f1f5f9' }}
              title="Recenter Map"
            >
              <i className="fa-solid fa-expand text-primary"></i>
              Recenter
            </button>
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2 mb-2 align-items-center">
          <button
            type="button"
            className={`btn btn-sm ${mapCategoryFilter === 'all' ? 'btn-dark' : 'btn-light border'} fw-semibold px-3 py-1`}
            style={{ borderRadius: '20px', fontSize: '0.72rem' }}
            onClick={() => setMapCategoryFilter('all')}
          >
            All - {validActiveUserMaps.length}{totalEmptyUserMapsCount > 0 ? ` (${totalEmptyUserMapsCount})` : ''}
          </button>
          {Object.entries(categoryStats).map(([cat, info]) => {
            const isActive = mapCategoryFilter.toLowerCase() === cat;
            const colorHex = info.colorHex || '#3b82f6';

            return (
              <button
                key={cat}
                type="button"
                className={`btn btn-sm ${isActive ? 'shadow-sm' : 'border'} fw-semibold px-3 py-1 d-flex align-items-center gap-2`}
                style={{
                  borderRadius: '20px',
                  fontSize: '0.72rem',
                  backgroundColor: isActive ? colorHex : '#ffffff',
                  color: isActive ? '#ffffff' : '#334155',
                  borderColor: colorHex,
                }}
                onClick={() => setMapCategoryFilter(cat)}
              >
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: isActive ? '#ffffff' : colorHex,
                    display: 'inline-block',
                  }}
                ></span>
                {cat.toUpperCase()} - {info.validCount}{info.emptyCount > 0 ? ` (${info.emptyCount})` : ''}
              </button>
            );
          })}
        </div>

        <div className="mb-3">
          <small className="text-muted" style={{ fontSize: '0.7rem' }}>
            <i className="fa-solid fa-circle-info me-1 text-primary"></i>
            Note: Numbers in parentheses <code>( )</code> represent active users whose locations are not detected.
          </small>
        </div>

        <div className="position-relative rounded-3 overflow-hidden border" style={{ height: '420px', background: '#f8fafc' }}>
          {filteredUserMaps.length === 0 ? (
            <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
              <i className="fa-solid fa-map-location-dot fa-3x mb-2 text-secondary opacity-50"></i>
              <span className="fw-medium" style={{ fontSize: '0.85rem' }}>No user location data to display</span>
            </div>
          ) : (
            <MapGL
              ref={mapRef}
              mapboxAccessToken={MAPBOX_TOKEN}
              initialViewState={{
                latitude: filteredUserMaps[0]?.latitude || -2.548926,
                longitude: filteredUserMaps[0]?.longitude || 118.0148634,
                zoom: 4.5,
              }}
              style={{ width: '100%', height: '100%' }}
              mapStyle="mapbox://styles/mapbox/streets-v12"
              onLoad={handleFitMapBounds}
            >
              {filteredUserMaps.map((u) => {
                const markerIconSrc = u.icon
                  ? u.icon.startsWith('/') || u.icon.startsWith('http')
                    ? u.icon
                    : `/assets/markers/${u.icon}`
                  : '/assets/markers/icon-marker-green.svg';

                return (
                  <Marker
                    key={u.id}
                    latitude={u.latitude}
                    longitude={u.longitude}
                    anchor="bottom"
                    onClick={(e) => {
                      e.originalEvent.stopPropagation();
                      setSelectedUserMap(u);
                      mapRef.current?.flyTo({
                        center: [u.longitude, u.latitude],
                        zoom: 13,
                        duration: 600,
                      });
                    }}
                  >
                    <img
                      src={markerIconSrc}
                      alt={u.name}
                      style={{ width: 18, height: 18, cursor: 'pointer' }}
                      title={`${u.name} (${u.category})`}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/assets/markers/icon-marker-green.svg';
                      }}
                    />
                  </Marker>
                );
              })}

              {selectedUserMap && selectedUserMap.latitude && selectedUserMap.longitude && (
                <Popup
                  latitude={selectedUserMap.latitude}
                  longitude={selectedUserMap.longitude}
                  anchor="bottom"
                  offset={[0, -30]}
                  onClose={() => setSelectedUserMap(null)}
                  closeOnClick={false}
                  maxWidth="260px"
                >
                  <div style={{ padding: '4px' }}>
                    <div className="d-flex align-items-center justify-content-between mb-2 gap-2">
                      <h6 className="fw-bold text-dark mb-0 text-truncate" style={{ fontSize: '0.85rem' }}>
                        {selectedUserMap.name}
                      </h6>
                      <span
                        className="badge text-white px-2 py-1"
                        style={{
                          backgroundColor: selectedUserMap.colorHex || '#66BB6A',
                          fontSize: '0.65rem',
                          borderRadius: '4px',
                          textTransform: 'uppercase',
                        }}
                      >
                        {selectedUserMap.category}
                      </span>
                    </div>

                    <div className="d-flex flex-column gap-1" style={{ fontSize: '0.72rem', color: '#475569' }}>
                      {selectedUserMap.phone && (
                        <div className="d-flex align-items-center gap-2">
                          <i className="fa-solid fa-phone text-muted" style={{ width: '12px' }}></i>
                          <span>{selectedUserMap.phone}</span>
                        </div>
                      )}
                      {selectedUserMap.email && (
                        <div className="d-flex align-items-center gap-2">
                          <i className="fa-solid fa-envelope text-muted" style={{ width: '12px' }}></i>
                          <span className="text-truncate" style={{ maxWidth: '180px' }}>{selectedUserMap.email}</span>
                        </div>
                      )}
                      {selectedUserMap.lastAccessAt && (
                        <div className="d-flex align-items-center gap-2">
                          <i className="fa-solid fa-clock text-muted" style={{ width: '12px' }}></i>
                          <span>{selectedUserMap.lastAccessAt}</span>
                        </div>
                      )}
                      <div className="d-flex align-items-center gap-2 text-muted mt-1 pt-1 border-top" style={{ fontSize: '0.68rem' }}>
                        <i className="fa-solid fa-location-dot" style={{ width: '12px' }}></i>
                        <span>{selectedUserMap.latitude.toFixed(6)}, {selectedUserMap.longitude.toFixed(6)}</span>
                      </div>
                    </div>
                  </div>
                </Popup>
              )}
            </MapGL>
          )}
        </div>
      </div>

      {/* Row 1: High Level Counters */}
      <div className="row g-3 mb-4">
        {/* Active last 1 Hour */}
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="growth-card p-3 shadow-sm h-100 d-flex flex-column justify-content-between">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.75px' }}>
                Active (1 Hour)
              </span>
              <span className="badge bg-light text-success border border-success-subtle px-2 py-1 d-flex align-items-center gap-1" style={{ fontSize: '0.65rem' }}>
                <span className="pulse-dot"></span> Live
              </span>
            </div>
            <div>
              <h3 className="fw-bold mb-1 text-dark" style={{ letterSpacing: '-0.75px' }}>
                {metricsData.activeLastHour}
              </h3>
              <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>
                Active users interacting in past 60m
              </p>
            </div>
          </div>
        </div>

        {/* Daily Active Users (DAU) */}
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="growth-card p-3 shadow-sm h-100 d-flex flex-column justify-content-between">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.75px' }}>
                Daily Active (DAU)
              </span>
              <div className="rounded-3 p-1 px-2 d-flex align-items-center gap-1" style={{ background: COLORS.primaryLight, color: COLORS.primary, width: 'fit-content' }}>
                <i className="fa-solid fa-user-check" style={{ fontSize: '0.85rem' }}></i>
              </div>
            </div>
            <div>
              <h3 className="fw-bold mb-1 text-dark" style={{ letterSpacing: '-0.75px' }}>
                {metricsData.dau.toLocaleString('id-ID')}
              </h3>
              <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>
                Unique active users today
              </p>
            </div>
          </div>
        </div>

        {/* Weekly Active Users (WAU) */}
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="growth-card p-3 shadow-sm h-100 d-flex flex-column justify-content-between">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.75px' }}>
                Weekly Active (WAU)
              </span>
              <div className="rounded-3 p-1 px-2 d-flex align-items-center gap-1" style={{ background: COLORS.purpleLight, color: COLORS.purple, width: 'fit-content' }}>
                <i className="fa-solid fa-users" style={{ fontSize: '0.85rem' }}></i>
              </div>
            </div>
            <div>
              <h3 className="fw-bold mb-1 text-dark" style={{ letterSpacing: '-0.75px' }}>
                {metricsData.wau.toLocaleString('id-ID')}
              </h3>
              <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>
                Active users this week
              </p>
            </div>
          </div>
        </div>

        {/* Monthly Active Users (MAU) */}
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="growth-card p-3 shadow-sm h-100 d-flex flex-column justify-content-between">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.75px' }}>
                Monthly Active (MAU)
              </span>
              <div className="rounded-3 p-1 px-2 d-flex align-items-center gap-1" style={{ background: COLORS.successLight, color: COLORS.success, width: 'fit-content' }}>
                <i className="fa-solid fa-calendar-check" style={{ fontSize: '0.85rem' }}></i>
              </div>
            </div>
            <div>
              <h3 className="fw-bold mb-1 text-dark" style={{ letterSpacing: '-0.75px' }}>
                {metricsData.mau.toLocaleString('id-ID')}
              </h3>
              <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>
                Active users this month
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Platform Split (100% Width) */}
      <div className="row g-3 mb-4">
        {/* Mobile vs Web usage */}
        <div className="col-12">
          <div className="growth-card p-3 shadow-sm" style={{ borderLeft: `4px solid ${COLORS.info}` }}>
            <div className="d-flex flex-column justify-content-between h-100">
              <span className="text-muted text-uppercase fw-bold d-block mb-3" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                Platform Split (Active)
              </span>

              {userType === 'perfectStore' ? (
                <>
                  <div className="d-flex justify-content-between align-items-baseline mb-2">
                    <div>
                      <span className="text-dark fw-bold" style={{ fontSize: '1.05rem' }}>
                        {metricsData.mobileActive.toLocaleString('id-ID')}
                      </span>
                      <small className="text-muted ms-1" style={{ fontSize: '0.7rem' }}>Mobile</small>
                    </div>
                    <div>
                      <span className="text-dark fw-bold" style={{ fontSize: '1.05rem' }}>
                        {metricsData.webActive.toLocaleString('id-ID')}
                      </span>
                      <small className="text-muted ms-1" style={{ fontSize: '0.7rem' }}>Web</small>
                    </div>
                  </div>

                  {/* Dual color progress bar */}
                  <div className="progress-micro">
                    <div
                      className="progress-micro-fill float-start"
                      style={{
                        width: `${(metricsData.mobileActive + metricsData.webActive) > 0 ? (metricsData.mobileActive / (metricsData.mobileActive + metricsData.webActive) * 100).toFixed(1) : 0}%`,
                        backgroundColor: COLORS.info
                      }}
                    ></div>
                    <div
                      className="progress-micro-fill float-start"
                      style={{
                        width: `${(metricsData.mobileActive + metricsData.webActive) > 0 ? (metricsData.webActive / (metricsData.mobileActive + metricsData.webActive) * 100).toFixed(1) : 0}%`,
                        backgroundColor: COLORS.purple
                      }}
                    ></div>
                  </div>
                  <div className="d-flex justify-content-between mt-1" style={{ fontSize: '0.65rem', color: COLORS.grayText }}>
                    <span>Mobile App ({(metricsData.mobileActive + metricsData.webActive) > 0 ? (metricsData.mobileActive / (metricsData.mobileActive + metricsData.webActive) * 100).toFixed(0) : 0}%)</span>
                    <span>Website ({(metricsData.mobileActive + metricsData.webActive) > 0 ? (metricsData.webActive / (metricsData.mobileActive + metricsData.webActive) * 100).toFixed(0) : 0}%)</span>
                  </div>
                </>
              ) : (
                <>
                  <h4 className="fw-bold text-dark mb-1" style={{ letterSpacing: '-0.5px' }}>
                    100% Mobile
                  </h4>
                  <small className="text-muted block">
                    All officers use the Mobile Android app
                  </small>

                  <div className="progress-micro mt-2">
                    <div
                      className="progress-micro-fill"
                      style={{ width: '100%', backgroundColor: COLORS.info }}
                    ></div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Field Officer Grade Composition (Only for More Access) */}
      {userType === 'moreAccess' && dashboardData?.gradeComposition && (
        <div className="card border-0 shadow-sm mb-4 p-4" style={{ borderRadius: '16px', background: '#ffffff' }}>
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
            <div>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.75px' }}>
                  Field Officer Performance Grading
                </span>
                <span className="badge bg-light text-primary border px-2 py-1" style={{ fontSize: '0.7rem' }}>
                  <i className="fa-solid fa-award me-1"></i>
                  {dashboardData.gradeComposition.totalEvaluated.toLocaleString('id-ID')} Evaluated Users
                </span>
              </div>
              <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>
                Overall competency grade distribution across active Field Officers
              </p>
            </div>

            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Average Score:</span>
              <span className="fw-bold fs-5 text-dark">
                {dashboardData.gradeComposition.averageScore.toFixed(1)}
              </span>
              <span className="text-muted small">/ 100 Pts</span>
            </div>
          </div>

          {/* 4 Grade Cards Grid */}
          <div className="row g-3 mb-3">
            {dashboardData.gradeComposition.distribution.map((item) => {
              const bgSoft = (() => {
                if (item.letter === 'A') return '#F0FDF4';
                if (item.letter === 'B') return '#FEFCE8';
                if (item.letter === 'C') return '#FFF7ED';
                return '#FEF2F2';
              })();
              const borderSoft = (() => {
                if (item.letter === 'A') return '#BBF7D0';
                if (item.letter === 'B') return '#FEF08A';
                if (item.letter === 'C') return '#FED7AA';
                return '#FECACA';
              })();

              return (
                <div key={item.grade} className="col-12 col-sm-6 col-lg-3">
                  <div
                    className="p-3 rounded-3 border h-100 d-flex flex-column justify-content-between"
                    style={{ backgroundColor: bgSoft, borderColor: borderSoft }}
                  >
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span
                          className="badge px-2.5 py-1 text-white fw-bold rounded-pill shadow-xs"
                          style={{ backgroundColor: item.color, fontSize: '0.75rem' }}
                        >
                          Grade {item.letter}
                        </span>
                        <span className="fw-bold small" style={{ color: item.color }}>
                          {item.percentage.toFixed(1)}%
                        </span>
                      </div>
                      <div className="fw-bold text-dark fs-4 mb-0">
                        {item.count.toLocaleString('id-ID')} <span className="text-muted fs-6 fw-normal">Officers</span>
                      </div>
                    </div>

                    <div className="mt-2 pt-2 border-top" style={{ borderColor: borderSoft }}>
                      <div className="fw-semibold text-dark small" style={{ fontSize: '0.75rem' }}>
                        {item.label}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Multi-segment visual stacked progress bar */}
          <div>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="text-muted small fw-semibold" style={{ fontSize: '0.7rem' }}>
                Distribution Ratio
              </span>
            </div>
            <div className="progress" style={{ height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
              {dashboardData.gradeComposition.distribution.map((item) => (
                <div
                  key={`bar-${item.grade}`}
                  className="progress-bar"
                  role="progressbar"
                  style={{
                    width: `${item.percentage}%`,
                    backgroundColor: item.color,
                  }}
                  title={`${item.grade}: ${item.count} (${item.percentage}%)`}
                />
              ))}
            </div>
            <div className="d-flex flex-wrap gap-3 mt-2" style={{ fontSize: '0.7rem' }}>
              {dashboardData.gradeComposition.distribution.map((item) => (
                <div key={`legend-${item.grade}`} className="d-flex align-items-center gap-1.5 text-muted">
                  <span className="rounded-circle" style={{ width: 8, height: 8, backgroundColor: item.color, display: 'inline-block' }}></span>
                  <span className="fw-semibold text-dark">{item.grade}</span>
                  <span>({item.count} • {item.percentage}%)</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Row 3: Full Width Growth & Trend Chart */}
      <div className="row g-4 mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm p-4 h-100" style={{ borderRadius: '16px', background: '#ffffff', minHeight: '380px' }}>
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <div>
                <span className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.75px' }}>
                  User Activity & Growth Trend
                </span>
              </div>
            </div>

            <div className="flex-grow-1" style={{ position: 'relative', height: '300px' }}>
              <Line data={trendChartData} options={trendChartOptions} plugins={[verticalHoverPlugin]} />
            </div>
          </div>
        </div>
      </div>

      {/* Specs Breakdown Section Header */}
      <div className="mb-2 d-flex justify-content-between align-items-center mt-2">
        <div>
          <span className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.75px' }}>
            Specifications Breakdown
          </span>
        </div>
        <div className="form-check form-switch d-flex align-items-center gap-2 m-0">
          <input
            className="form-check-input"
            type="checkbox"
            id="showUnknownToggle"
            checked={showUnknown}
            onChange={(e) => setShowUnknown(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          <label className="form-check-label text-muted fw-semibold" htmlFor="showUnknownToggle" style={{ fontSize: '0.75rem', cursor: 'pointer', userSelect: 'none' }}>
            Show Unknown
          </label>
        </div>
      </div>

      {/* Row 4: Specs Breakdown (2x2 Grid) */}
      <div className="row g-4 mb-4">
        {/* Device Brand Card */}
        <div className="col-12 col-lg-6">
          <div className="card border-0 shadow-sm p-4 h-100" style={{ borderRadius: '16px', background: '#ffffff', minHeight: '240px' }}>
            <span className="text-muted text-uppercase fw-bold mb-3 d-block" style={{ fontSize: '0.7rem', letterSpacing: '0.75px' }}>
              Device Brand
            </span>
            <div className="row align-items-center w-100 g-3 flex-grow-1 m-0">
              {/* Chart Column */}
              <div className="col-12 col-xl-4 d-flex justify-content-center justify-content-xl-start p-0">
                <div style={{ width: '120px', height: '120px', position: 'relative', flexShrink: 0 }}>
                  <Doughnut data={brandChartData} options={deviceChartOptions} />
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                    }}
                  >
                    <span className="text-muted" style={{ fontSize: '0.55rem', display: 'block', textTransform: 'uppercase' }}>
                      Active
                    </span>
                    <strong style={{ fontSize: '0.95rem', color: COLORS.darkText, lineHeight: 1 }}>
                      {brandTotalCount.toLocaleString('id-ID')}
                    </strong>
                  </div>
                </div>
              </div>

              {/* Legend Column */}
              <div className="col-12 col-xl-8 p-0 ps-xl-3 mt-3 mt-xl-0">
                <div className="row g-2 m-0" style={{ fontSize: '0.72rem' }}>
                  {brandData.map((d, index) => (
                    <div key={index} className="col-6">
                      <div className="d-flex align-items-center justify-content-between border-bottom pb-1">
                        <div className="d-flex align-items-center gap-2 text-truncate" style={{ maxWidth: '60%' }}>
                          <span
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: d.color,
                              display: 'inline-block',
                              flexShrink: 0,
                            }}
                          ></span>
                          <span className="text-dark fw-medium text-truncate" title={d.device}>{d.device}</span>
                        </div>
                        <span className="text-muted text-nowrap ms-2" style={{ fontSize: '0.68rem' }}>
                          {d.count.toLocaleString('id-ID')} ({d.percentage}%)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* OS Version Card */}
        <div className="col-12 col-lg-6">
          <div className="card border-0 shadow-sm p-4 h-100" style={{ borderRadius: '16px', background: '#ffffff', minHeight: '240px' }}>
            <span className="text-muted text-uppercase fw-bold mb-3 d-block" style={{ fontSize: '0.7rem', letterSpacing: '0.75px' }}>
              OS Version
            </span>
            <div className="row align-items-center w-100 g-3 flex-grow-1 m-0">
              {/* Chart Column */}
              <div className="col-12 col-xl-4 d-flex justify-content-center justify-content-xl-start p-0">
                <div style={{ width: '120px', height: '120px', position: 'relative', flexShrink: 0 }}>
                  <Doughnut data={osChartData} options={deviceChartOptions} />
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                    }}
                  >
                    <span className="text-muted" style={{ fontSize: '0.55rem', display: 'block', textTransform: 'uppercase' }}>
                      Active
                    </span>
                    <strong style={{ fontSize: '0.95rem', color: COLORS.darkText, lineHeight: 1 }}>
                      {osTotalCount.toLocaleString('id-ID')}
                    </strong>
                  </div>
                </div>
              </div>

              {/* Legend Column */}
              <div className="col-12 col-xl-8 p-0 ps-xl-3 mt-3 mt-xl-0">
                <div className="row g-2 m-0" style={{ fontSize: '0.72rem' }}>
                  {osData.map((d, index) => (
                    <div key={index} className="col-6">
                      <div className="d-flex align-items-center justify-content-between border-bottom pb-1">
                        <div className="d-flex align-items-center gap-2 text-truncate" style={{ maxWidth: '60%' }}>
                          <span
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: d.color,
                              display: 'inline-block',
                              flexShrink: 0,
                            }}
                          ></span>
                          <span className="text-dark fw-medium text-truncate" title={d.device}>{d.device}</span>
                        </div>
                        <span className="text-muted text-nowrap ms-2" style={{ fontSize: '0.68rem' }}>
                          {d.count.toLocaleString('id-ID')} ({d.percentage}%)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* App Version Card */}
        <div className="col-12 col-lg-6">
          <div className="card border-0 shadow-sm p-4 h-100" style={{ borderRadius: '16px', background: '#ffffff', minHeight: '240px' }}>
            <span className="text-muted text-uppercase fw-bold mb-3 d-block" style={{ fontSize: '0.7rem', letterSpacing: '0.75px' }}>
              App Version
            </span>
            <div className="row align-items-center w-100 g-3 flex-grow-1 m-0">
              {/* Chart Column */}
              <div className="col-12 col-xl-4 d-flex justify-content-center justify-content-xl-start p-0">
                <div style={{ width: '120px', height: '120px', position: 'relative', flexShrink: 0 }}>
                  <Doughnut data={appVersionChartData} options={deviceChartOptions} />
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                    }}
                  >
                    <span className="text-muted" style={{ fontSize: '0.55rem', display: 'block', textTransform: 'uppercase' }}>
                      Active
                    </span>
                    <strong style={{ fontSize: '0.95rem', color: COLORS.darkText, lineHeight: 1 }}>
                      {appTotalCount.toLocaleString('id-ID')}
                    </strong>
                  </div>
                </div>
              </div>

              {/* Legend Column */}
              <div className="col-12 col-xl-8 p-0 ps-xl-3 mt-3 mt-xl-0">
                <div className="row g-2 m-0" style={{ fontSize: '0.72rem' }}>
                  {appVersionData.map((d, index) => (
                    <div key={index} className="col-6">
                      <div className="d-flex align-items-center justify-content-between border-bottom pb-1">
                        <div className="d-flex align-items-center gap-2 text-truncate" style={{ maxWidth: '60%' }}>
                          <span
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: d.color,
                              display: 'inline-block',
                              flexShrink: 0,
                            }}
                          ></span>
                          <span className="text-dark fw-medium text-truncate" title={d.device}>{d.device}</span>
                        </div>
                        <span className="text-muted text-nowrap ms-2" style={{ fontSize: '0.68rem' }}>
                          {d.count.toLocaleString('id-ID')} ({d.percentage}%)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Disbursement Bank Card */}
        <div className="col-12 col-lg-6">
          <div className="card border-0 shadow-sm p-4 h-100" style={{ borderRadius: '16px', background: '#ffffff', minHeight: '240px' }}>
            <span className="text-muted text-uppercase fw-bold mb-3 d-block" style={{ fontSize: '0.7rem', letterSpacing: '0.75px' }}>
              Disbursement Bank
            </span>
            <div className="row align-items-center w-100 g-3 flex-grow-1 m-0">
              {/* Chart Column */}
              <div className="col-12 col-xl-4 d-flex justify-content-center justify-content-xl-start p-0">
                <div style={{ width: '120px', height: '120px', position: 'relative', flexShrink: 0 }}>
                  <Doughnut data={bankChartData} options={deviceChartOptions} />
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                    }}
                  >
                    <span className="text-muted" style={{ fontSize: '0.55rem', display: 'block', textTransform: 'uppercase' }}>
                      Active
                    </span>
                    <strong style={{ fontSize: '0.95rem', color: COLORS.darkText, lineHeight: 1 }}>
                      {bankTotalCount.toLocaleString('id-ID')}
                    </strong>
                  </div>
                </div>
              </div>

              {/* Legend Column */}
              <div className="col-12 col-xl-8 p-0 ps-xl-3 mt-3 mt-xl-0">
                <div className="row g-2 m-0" style={{ fontSize: '0.72rem' }}>
                  {bankData.map((d, index) => (
                    <div key={index} className="col-6">
                      <div className="d-flex align-items-center justify-content-between border-bottom pb-1">
                        <div className="d-flex align-items-center gap-2 text-truncate" style={{ maxWidth: '60%' }}>
                          <span
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: d.color,
                              display: 'inline-block',
                              flexShrink: 0,
                            }}
                          ></span>
                          <span className="text-dark fw-medium text-truncate" title={d.device}>{d.device}</span>
                        </div>
                        <span className="text-muted text-nowrap ms-2" style={{ fontSize: '0.68rem' }}>
                          {d.count.toLocaleString('id-ID')} ({d.percentage}%)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Row 4: Regional Analysis (Commented out)
      <div className="row g-4 mt-2">
        <div className="col-12">
          <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '16px', background: '#ffffff' }}>
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <div>
                <span className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.75px' }}>
                  Regional User Engagement
                </span>
              </div>
              <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                Sorted by active user count
              </span>
            </div>

            <div className="table-responsive">
              <table className="table table-custom align-middle border-0 mb-0" style={{ fontSize: '0.85rem' }}>
                <thead>
                  <tr className="text-muted border-bottom" style={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>
                    <th className="py-3 border-0 ps-0" style={{ fontWeight: '700' }}>Daerah / Provinsi</th>
                    <th className="py-3 border-0 text-end" style={{ fontWeight: '700' }}>Active Users</th>
                    <th className="py-3 border-0 text-end" style={{ fontWeight: '700' }}>Total Registered</th>
                    <th className="py-3 border-0 ps-5" style={{ fontWeight: '700', width: '35%' }}>Distribution %</th>
                  </tr>
                </thead>
                <tbody>
                  {regionalData.map((reg, index) => (
                    <tr key={index} className="border-bottom">
                      <td className="py-3 border-0 ps-0 fw-semibold text-dark">
                        {reg.name}
                      </td>
                      <td className="py-3 border-0 text-end fw-bold text-dark">
                        {reg.activeCount.toLocaleString('id-ID')}
                      </td>
                      <td className="py-3 border-0 text-end text-muted">
                        {reg.totalCount.toLocaleString('id-ID')}
                      </td>
                      <td className="py-3 border-0 ps-5">
                        <div className="d-flex align-items-center gap-3">
                          <span className="fw-semibold text-dark" style={{ minWidth: '35px' }}>
                            {reg.percentage}%
                          </span>
                          <div className="progress-micro flex-grow-1">
                            <div
                              className="progress-micro-fill"
                              style={{
                                width: `${reg.percentage}%`,
                                backgroundColor: index === 0 ? COLORS.primary : index === 1 ? COLORS.success : COLORS.purple,
                              }}
                            ></div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      */}
    </div>
  );
};

export default UserGrowthMonitor;
